<script setup>
/**
 * 回合时间线主图：横轴是真实毫秒比例的时间，纵向是泳道（玩家看到的 / 玩家听到的 / 后端事件 / 模型调用）。
 * 每条泳道上面一行是时刻（点），下面若干行是耗时（色块）。排版全部在 timeline-layout.js 里算好，这里只负责画和鼠标读数。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { SegmentedControl } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { axisDurationMs, estimateTextWidth, layoutTimeline } from './timeline-layout.js'

const props = defineProps({
  timeline: { type: Object, required: true },
})

const LEFT = 104
const RIGHT = 28
const AXIS_HEIGHT = 26
const METRIC_ROW_HEIGHT = 18
const LANE_PADDING = 10
const MARKER_LABEL_HEIGHT = 15
const MARKER_LINE_HEIGHT = 14
const BAR_LABEL_HEIGHT = 14
const BAR_HEIGHT = 20
const BAR_ROW_GAP = 6
const BAR_ROW_HEIGHT = BAR_LABEL_HEIGHT + BAR_HEIGHT + BAR_ROW_GAP
const ZOOM_KEYS = { 1: 'fit', 2: 'x2', 4: 'x4' }

const { t, te } = useI18n()
const wrapper = ref(null)
const wrapperWidth = ref(1100)
const zoom = ref('1')
const hoverMs = ref(null)
let resizeObserver = null

const zoomOptions = computed(() => Object.entries(ZOOM_KEYS).map(([value, key]) => ({
  value,
  label: t(`visualGameAdmin.llm.turnTimelines.chart.zoom.${key}`),
})))

/** 角色、节点名是开放集合：有翻译用翻译，没有就显示原名。 */
function label(key, params, fallback) {
  const path = `visualGameAdmin.llm.turnTimelines.chart.${key}`
  return te(path) ? t(path, params) : fallback
}

const scale = computed(() => (wrapperWidth.value - LEFT - RIGHT) / axisDurationMs(props.timeline) * Number(zoom.value))
const layout = computed(() => layoutTimeline(props.timeline, { scale: scale.value, label }))

const geometry = computed(() => {
  const top = layout.value.metricRows * METRIC_ROW_HEIGHT + AXIS_HEIGHT
  let y = top
  const lanes = layout.value.lanes.map(lane => {
    // 「玩家看到的」要有一行来铺空档，哪怕这一回合没有任何视频或文字段
    const barRows = lane.key === 'screen' && layout.value.gaps.length > 0 ? Math.max(1, lane.barRows) : lane.barRows
    const markersHeight = lane.markers.length === 0 ? 0 : lane.markerRows * MARKER_LABEL_HEIGHT + MARKER_LINE_HEIGHT
    const barsTop = y + LANE_PADDING + markersHeight
    const height = LANE_PADDING + markersHeight + barRows * BAR_ROW_HEIGHT + LANE_PADDING
    const placed = { ...lane, y, height, barRows, barsTop, markerLineY: y + LANE_PADDING + lane.markerRows * MARKER_LABEL_HEIGHT + MARKER_LINE_HEIGHT / 2 }
    y += height
    return placed
  })
  return { top, lanes, height: y + 6, width: LEFT + layout.value.durationMs * scale.value + RIGHT }
})

const screenLane = computed(() => geometry.value.lanes.find(lane => lane.key === 'screen') ?? null)

function x(ms) {
  return LEFT + ms * scale.value
}

function markerLabelY(lane, item) {
  return lane.y + LANE_PADDING + (lane.markerRows - item.row) * MARKER_LABEL_HEIGHT - 4
}

function barY(lane, item) {
  return lane.barsTop + item.row * BAR_ROW_HEIGHT + BAR_LABEL_HEIGHT
}

function chipWidth(text) {
  return estimateTextWidth(text) + 10
}

function onMove(event) {
  const bounds = event.currentTarget.getBoundingClientRect()
  const ms = (event.clientX - bounds.left - LEFT) / scale.value
  hoverMs.value = ms < 0 || ms > layout.value.durationMs ? null : ms
}

onMounted(() => {
  resizeObserver = new ResizeObserver(entries => { wrapperWidth.value = entries[0].contentRect.width })
  resizeObserver.observe(wrapper.value)
})
onBeforeUnmount(() => resizeObserver.disconnect())
</script>

<template>
  <div class="timeline-chart">
    <div class="timeline-chart__bar">
      <ul class="timeline-chart__legend">
        <li><i class="swatch swatch--wait" />{{ t('visualGameAdmin.llm.turnTimelines.chart.legend.wait') }}</li>
        <li><i class="swatch swatch--generate" />{{ t('visualGameAdmin.llm.turnTimelines.chart.legend.generate') }}</li>
        <li><i class="swatch swatch--video" />{{ t('visualGameAdmin.llm.turnTimelines.chart.legend.video') }}</li>
        <li><i class="swatch swatch--idle" />{{ t('visualGameAdmin.llm.turnTimelines.chart.legend.idle') }}</li>
        <li><i class="swatch swatch--metric" />{{ t('visualGameAdmin.llm.turnTimelines.chart.legend.metric') }}</li>
      </ul>
      <SegmentedControl v-model="zoom" :options="zoomOptions" size="sm" />
    </div>

    <div ref="wrapper" class="timeline-chart__scroll">
      <!-- 泳道名单独一列并吸在左边：放大后横向滚动时仍然看得到每条泳道是什么 -->
      <div class="lane-names" :style="{ width: `${LEFT - 8}px`, height: `${geometry.height}px` }">
        <span
          v-for="(lane, index) in geometry.lanes"
          :key="lane.key"
          :class="['lane-names__item', { 'lane-names__item--alt': index % 2 === 1 }]"
          :style="{ top: `${lane.y}px`, height: `${lane.height}px` }"
        >{{ t(`visualGameAdmin.llm.turnTimelines.chart.lanes.${lane.key}`) }}</span>
      </div>
      <svg
        :width="geometry.width"
        :height="geometry.height"
        :viewBox="`0 0 ${geometry.width} ${geometry.height}`"
        role="img"
        :aria-label="t('visualGameAdmin.llm.turnTimelines.chart.ariaLabel')"
        data-testid="turn-timeline-chart"
        @mousemove="onMove"
        @mouseleave="hoverMs = null"
      >
        <defs>
          <pattern id="turn-timeline-idle" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line class="idle-stripe" x1="0" y1="0" x2="0" y2="7" />
          </pattern>
        </defs>

        <!-- 泳道底 -->
        <rect
          v-for="(lane, index) in geometry.lanes"
          :key="lane.key"
          :class="['lane-bg', { 'lane-bg--alt': index % 2 === 1 }]"
          x="0"
          :y="lane.y"
          :width="geometry.width"
          :height="lane.height"
        />

        <!-- 时间刻度 -->
        <g v-for="tick in layout.ticks" :key="tick.ms">
          <line class="grid-line" :x1="x(tick.ms)" :x2="x(tick.ms)" :y1="geometry.top - 6" :y2="geometry.height - 6" />
          <text class="tick-label" :x="x(tick.ms)" :y="geometry.top - 10" text-anchor="middle">{{ tick.label }}</text>
        </g>

        <!-- 空档：没有任何东西在播、也没有新东西出现。铺在「玩家看到的」泳道的色块区 -->
        <g v-if="screenLane !== null">
          <g v-for="(gap, index) in layout.gaps" :key="`gap-${index}`">
            <title>{{ gap.title }}</title>
            <rect
              class="idle-span"
              :x="x(gap.start_ms)"
              :y="screenLane.barsTop + BAR_LABEL_HEIGHT"
              :width="x(gap.end_ms) - x(gap.start_ms)"
              :height="BAR_HEIGHT"
            />
            <template v-if="gap.text !== null">
              <rect
                class="gap-chip"
                :x="(x(gap.start_ms) + x(gap.end_ms)) / 2 - chipWidth(gap.text) / 2"
                :y="screenLane.barsTop + BAR_LABEL_HEIGHT + 2"
                :width="chipWidth(gap.text)"
                :height="BAR_HEIGHT - 4"
                rx="3"
              />
              <text class="gap-text" :x="(x(gap.start_ms) + x(gap.end_ms)) / 2" :y="screenLane.barsTop + BAR_LABEL_HEIGHT + 14" text-anchor="middle">{{ gap.text }}</text>
            </template>
          </g>
        </g>

        <!-- 每条泳道：上面一行时刻（点），下面若干行耗时（色块） -->
        <g v-for="lane in geometry.lanes" :key="`items-${lane.key}`">
          <line v-if="lane.markers.length > 0" class="marker-line" :x1="LEFT" :x2="x(layout.durationMs)" :y1="lane.markerLineY" :y2="lane.markerLineY" />
          <g v-for="(item, index) in lane.markers" :key="`m-${index}`" :class="['item', `item--${item.tone}`]">
            <title>{{ item.title }}</title>
            <line class="leader" :x1="x(item.at_ms)" :x2="x(item.at_ms)" :y1="markerLabelY(lane, item) + 3" :y2="lane.markerLineY" />
            <circle class="dot" :cx="x(item.at_ms)" :cy="lane.markerLineY" r="4.5" />
            <text class="item-label" :x="x(item.at_ms) + 5" :y="markerLabelY(lane, item)">{{ item.text }}</text>
          </g>
          <g v-for="(item, index) in lane.bars" :key="`b-${index}`" :class="['item', `item--${item.tone}`, { 'item--light': item.light }]">
            <title>{{ item.title }}</title>
            <text class="item-label" :x="x(item.start_ms)" :y="barY(lane, item) - 4">{{ item.label }}</text>
            <rect
              v-if="item.split_ms !== null"
              class="bar bar--wait"
              :x="x(item.start_ms)"
              :y="barY(lane, item)"
              :width="Math.max(0, x(item.split_ms) - x(item.start_ms))"
              :height="BAR_HEIGHT"
            />
            <rect
              class="bar bar--solid"
              :x="x(item.split_ms ?? item.start_ms)"
              :y="barY(lane, item)"
              :width="item.split_ms === null ? item.width_px : Math.max(2, x(item.end_ms) - x(item.split_ms))"
              :height="BAR_HEIGHT"
            />
            <text v-if="item.wait_text" class="bar-text bar-text--wait" :x="x(item.start_ms) + 6" :y="barY(lane, item) + 14">{{ item.wait_text }}</text>
            <text v-if="item.generate_text" class="bar-text bar-text--solid" :x="x(item.split_ms) + 6" :y="barY(lane, item) + 14">{{ item.generate_text }}</text>
          </g>
        </g>

        <!-- 四个关键时刻：贯穿全图的竖线 -->
        <g v-for="line in layout.metricLines" :key="line.key">
          <line class="metric-line" :x1="x(line.at_ms)" :x2="x(line.at_ms)" :y1="line.row * METRIC_ROW_HEIGHT + 14" :y2="geometry.height - 6" />
          <text class="metric-label" :x="x(line.at_ms) + 4" :y="line.row * METRIC_ROW_HEIGHT + 12">{{ line.text }}</text>
        </g>

        <!-- 鼠标读数 -->
        <g v-if="hoverMs !== null" class="cursor">
          <line class="cursor-line" :x1="x(hoverMs)" :x2="x(hoverMs)" :y1="geometry.top - 6" :y2="geometry.height - 6" />
          <rect class="cursor-chip" :x="x(hoverMs) - 26" :y="geometry.top - 24" width="52" height="16" rx="8" />
          <text class="cursor-text" :x="x(hoverMs)" :y="geometry.top - 12" text-anchor="middle">{{ (hoverMs / 1000).toFixed(2) }}s</text>
        </g>
      </svg>
    </div>
    <p class="timeline-chart__note">{{ t('visualGameAdmin.llm.turnTimelines.chart.note') }}</p>
  </div>
</template>

<style scoped>
.timeline-chart {
  display: grid;
  gap: var(--drama-space-md);
}

.timeline-chart__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.timeline-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm) var(--drama-space-lg);
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.timeline-chart__legend li {
  display: flex;
  align-items: center;
  gap: var(--drama-space-xs);
}

.timeline-chart__note {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.swatch {
  display: inline-block;
  width: 18px;
  height: 10px;
  border-radius: 2px;
}

.swatch--wait { background: var(--drama-color-info); opacity: 0.28; }
.swatch--generate { background: var(--drama-color-info); }
.swatch--video { background: var(--drama-color-primary); }
.swatch--idle { background: repeating-linear-gradient(45deg, var(--drama-color-danger) 0 2px, transparent 2px 6px); opacity: 0.6; }
.swatch--metric { width: 0; height: 12px; border-left: 2px dashed var(--drama-color-danger); border-radius: 0; }

.timeline-chart__scroll {
  overflow-x: auto;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface);
}

.lane-names {
  position: sticky;
  left: 0;
  z-index: 1;
  float: left;
  margin-right: -96px;
}

.lane-names__item {
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  padding-left: 12px;
  border-right: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
  color: var(--drama-color-text);
  font-size: 12px;
  font-weight: 600;
}

.lane-names__item--alt { background: var(--drama-color-surface-alt); }

svg { display: block; }
svg text { font-family: var(--drama-font-sans, inherit); }

.lane-bg { fill: var(--drama-color-surface); }
.lane-bg--alt { fill: var(--drama-color-surface-alt); }
.marker-line { stroke: var(--drama-color-border); stroke-width: 1; }
.grid-line { stroke: var(--drama-color-border); stroke-width: 1; stroke-dasharray: 2 4; }
.tick-label { fill: var(--drama-color-text-muted); font-size: 10.5px; }

.idle-stripe { stroke: var(--drama-color-danger); stroke-width: 2; opacity: 0.55; }
.idle-span { fill: url(#turn-timeline-idle); }
.gap-chip { fill: var(--drama-color-surface); opacity: 0.92; }
.gap-text { fill: var(--drama-color-danger); font-size: 11px; font-weight: 700; pointer-events: none; }

.item-label { fill: var(--drama-color-text); font-size: 11px; }
.leader { stroke: var(--drama-color-border-strong); stroke-width: 1; }
.bar { rx: 3; }
.bar--wait { opacity: 0.28; }
.item--light .bar--solid { opacity: 0.3; }
.dot { stroke: var(--drama-color-surface); stroke-width: 1.5; }
.item:hover .bar, .item:hover .dot { stroke: var(--drama-color-text); stroke-width: 1.5; }
.item:hover .item-label { font-weight: 600; }

.item--key .dot { fill: var(--drama-color-text); }
.item--text .dot, .item--text .bar { fill: var(--drama-color-success); }
.item--hint .dot { fill: var(--drama-color-info); }
.item--emit .dot { fill: var(--drama-color-text-muted); }
.item--video .bar { fill: var(--drama-color-primary); }
.item--cg .bar { fill: var(--drama-color-warning); }
.item--queue .bar { fill: var(--drama-color-text-muted); opacity: 0.35; }
.item--audio-npc .bar { fill: var(--drama-color-success); }
.item--audio-player .bar { fill: var(--drama-color-info); }
.item--parse-kp-core .bar { fill: var(--drama-color-info); }
.item--parse-state .bar { fill: var(--drama-color-warning); }
.item--parse-narration .bar { fill: var(--drama-color-info); }
.item--parse-npcLine .bar { fill: var(--drama-color-success); }
.item--parse-rest .bar { fill: var(--drama-color-text-muted); }
.item--model-kp .bar { fill: var(--drama-color-info); }
.item--model-npc .bar { fill: var(--drama-color-success); }
.item--model-action .bar { fill: var(--drama-color-warning); }
.item--model-other .bar { fill: var(--drama-color-primary); }
.item--tts .bar { fill: var(--drama-color-primary); opacity: 0.6; }

.bar-text { font-size: 11px; pointer-events: none; }
.bar-text--wait { fill: var(--drama-color-text); }
.bar-text--solid { fill: var(--drama-color-on-primary); font-weight: 600; }

.metric-line { stroke: var(--drama-color-danger); stroke-width: 1.5; stroke-dasharray: 5 4; }
.metric-label { fill: var(--drama-color-danger); font-size: 11px; font-weight: 600; }

.cursor-line { stroke: var(--drama-color-text); stroke-width: 1; opacity: 0.5; }
.cursor-chip { fill: var(--drama-color-text); }
.cursor-text { fill: var(--drama-color-surface); font-size: 10.5px; font-weight: 600; }
.cursor { pointer-events: none; }
</style>
